import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';

/**
 * Workflow service — drives long-running multi-order operations through the
 * orchestrator. Direct port of `/portal/src/app/services/workflowServices.js`.
 * POST /workflow/v1/action-jobs
 * body: { actionType, reason, orderIds, dryRun }
 * actionType is one of: ResumeProcessing | Cancel | SendToVendor | RetryPolling
 * Returns asynchronously — the job runs in the background and emits SSE
 * `orderProcessingStarted` / `orderProcessingCompleted` events as each order
 * is touched. Callers should optimistically invalidate the OrderList and
 * let SSE drive the eventual UI sync.
 */

export type BulkActionType = 'ResumeProcessing' | 'Cancel' | 'SendToVendor' | 'RetryPolling';

export interface CreateBulkActionJobPayload {
  actionType: BulkActionType;
  reason: string;
  orderIds: string[];
  dryRun?: boolean;
}

export interface CreateBulkActionJobResponse {
  /** Returned id of the queued workflow run. */
  jobId?: string;
  [key: string]: unknown;
}

export const workflowApi = createApi({
  reducerPath: 'workflowApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['BulkActionJob'],
  endpoints: (builder) => ({
    createBulkActionJob: builder.mutation<CreateBulkActionJobResponse, CreateBulkActionJobPayload>({
      query: (body) => ({
        url: `${services.workflow}/v1/action-jobs`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const { useCreateBulkActionJobMutation } = workflowApi;
